import routes from '~pages';

import { createRouter, createWebHistory } from 'vue-router';

import { canNavigate } from '@layouts/plugins/casl';
import { setupLayouts } from 'virtual:generated-layouts';

import { startNavigationProgress, finishNavigationProgress } from '@/composables/useNavigationProgress';
import { isConnectionsV2Enabled } from '@/config/connectionsV2';
import { isSellerFeedbackEnabled, SELLER_FEEDBACK_PATH } from '@/config/sellerFeedback';
import { redactSensitiveQuery } from '@/helpers/urlHelpers';
import ability from '@/plugins/casl/ability';
import { googleAnalyticsId } from '@/plugins/google-analytics';
import { bootstrap } from '@/services/auth/bootstrap';
import { inviteMismatch } from '@/services/auth/pendingInvite';
import { useAccountStore } from '@/stores/account.js';
import { useAppVersionStore } from '@/stores/appVersion';
import { useAuthStore } from '@/stores/auth.js';
import { useEnvironmentStore } from '@/stores/environment.js';
import { useViewportStore } from '@/stores/viewport';


import { devOnlyPaths, isDevOnlyPath, isDevOnlyPathAllowed, isEbayPagesAllowed } from './devOnlyPaths';
import { oauthQueryRescue } from './oauthQueryRescue';
import { settingsTargetsRedirect } from './performanceTargetsRedirect.js';
import { REDIRECT_REASON, redirectWithNotice } from './redirectNotices';
import { isTargetAcosPathBlocked } from './targetAcosGate.js';
import {
  connectAccountsRedirect,
  connectionsHubRedirect,
  getHomeRoute,
  isHardGateAllowedPath,
  loginRedirectWithReturn,
  resolveAuthedToRedirect,
  resolveInviteLinkWhileSignedIn,
  resolveRootEntry,
} from './utils';

// What resolveSignedInLanding needs from the app: the account store and the dev-only eBay gate.
const signedInLandingDeps = (authStore) => ({
  accountStore: useAccountStore(),
  ebayAllowed: isEbayPagesAllowed(authStore, useEnvironmentStore()),
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...setupLayouts(routes)],
});

// Start the top progress bar as early as possible — registered before the async guard
// below so it fires while that guard awaits bootstrap/redirects, giving immediate feedback
// on a slow (cold-chunk) navigation instead of a frozen-looking UI.
router.beforeEach(() => {
  startNavigationProgress();
});

// A Shopify claim token (#shopifyClaim=) leaves the URL before any other guard sees it: moved
// to the rescue copy, then the same location without the hash. Otherwise the first navigation
// restores the fragment to the address bar, and the login redirect copies it into ?to=.
router.beforeEach((to) => oauthQueryRescue.stripShopifyClaimHash(to));

router.beforeEach(async (to, from) => {
  // Wait for auth bootstrap (backend session restore) before the first routing decision.
  // ONLY the guard awaits this — never the axios interceptors (restore's own /users/me
  // request must pass while ready is still false, otherwise we'd deadlock).
  await bootstrap.waitForReady();

  // Amazon OAuth callback params are captured into sessionStorage by oauthQueryRescue
  // (run once in installAuth, before this guard's waitForReady resolves) — no inline capture here.

  // Only a breaking build forces a full page load; a non-breaking one waits for the user.
  if (useAppVersionStore().requiresReload) {
    window.location.href = to.fullPath;
    return false;
  }

  const authStore = useAuthStore();
  const isLoggedIn = authStore.isAuthenticated;

  // Clear profile-specific params when going to login or register so the new session doesn't
  // inherit potentially invalid IDs from the old one.
  const goingToAuth = to.name === 'login' || to.name === 'register';
  if (goingToAuth && (to.query.businessProfileId || to.query.profileId || to.query.marketplace)) {
    const queryWithoutProfiles = { ...to.query };
    delete queryWithoutProfiles.businessProfileId;
    delete queryWithoutProfiles.profileId;
    delete queryWithoutProfiles.marketplace;

    return { name: to.name, query: queryWithoutProfiles };
  }

  // Defense-in-depth: if the owned profile set is already loaded and the URL carries a
  // businessProfileId that isn't one of them (shared link / stale bookmark), strip it (and the
  // marketplace profileId, which belongs to that profile) before any resource request can mint a
  // token for it. Only acts once the set is loaded — on first load it isn't yet, and the account
  // store's sync validates + repairs the URL instead.
  if (isLoggedIn && to.query.businessProfileId) {
    const owned = useAccountStore().businessProfiles;
    const requested = +to.query.businessProfileId;
    if (owned?.length && !owned.some((p) => p.id === requested)) {
      const query = { ...to.query };
      delete query.businessProfileId;
      delete query.profileId;
      delete query.marketplace;
      return { name: to.name, params: to.params, query };
    }
  }

  const isInitial = !from.name && from.matched.length === 0;

  // Authed users: resolve the ?to= redirect on the initial navigation or while on an auth page.
  const isOnAuthPage = to.name === 'login' || to.name === 'register';
  // Registration verification / MFA setup owns its final Continue action, even on reload.
  if (isLoggedIn && to.query.to && !to.meta.deferAuthReturn && (isInitial || isOnAuthPage)) {
    return resolveAuthedToRedirect(to, { landing: signedInLandingDeps(authStore) });
  }

  // '/' is never rendered: login for anonymous, else an explicit ?to= and then the same landing as
  // sign-in (seller-only and eBay-only profiles open their own pages). Also covers the
  // redirectIfLoggedIn bounce below, which lands on '/'.
  if (to.path === '/') {
    return resolveRootEntry(
      to,
      isLoggedIn ? { isLoggedIn, ...signedInLandingDeps(authStore) } : { isLoggedIn },
    );
  }

  // Redirect /ppc/analytics to the campaign tab
  if (to.path === '/ppc/analytics') {
    return { name: 'ppc-analytics-tab', params: { tab: 'campaign' }, query: to.query };
  }

  // Legacy History tab URL — collapsed into a single /ppc/history page.
  if (to.path === '/ppc/history/local' || to.path === '/ppc/history/remote') {
    return { name: 'ppc-history', query: to.query };
  }

  const normalizedPath = to.path.toLowerCase();

  if (isTargetAcosPathBlocked(normalizedPath)) {
    return { ...getHomeRoute(), query: to.query };
  }

  // Bookmarks of Settings → Targets. Resolved here, before the page mounts, so it does not
  // race the business-profile query rewrite the way a setup router.replace does.
  const performanceTargets = settingsTargetsRedirect(to);
  if (performanceTargets) return performanceTargets;

  // Dev Tools (/dev/*) is the internal ops console — super admins only, in every
  // environment (it ships on production builds too). Hiding it from the nav
  // (superAdminOnly) is cosmetic; the route itself must be hard-blocked.
  if (normalizedPath === '/dev' || normalizedPath.startsWith('/dev/')) {
    if (!authStore.user?.superAdmin) {
      // Anonymous users get the silent redirect (getHomeRoute → login) — the notice copy
      // promises the Dashboard, which only holds for authenticated users.
      if (!isLoggedIn) return { ...getHomeRoute(), query: to.query };
      return redirectWithNotice(REDIRECT_REASON.DEV_SUPER_ADMIN, to, { blocking: !isInitial });
    }
  }

  // Dev-only routes (devOnlyPaths.js). Hiding them from the nav (envOnly: 'development')
  // is cosmetic — the route itself must be hard-blocked, otherwise any direct URL opens it on
  // production. Redirect to home when the effective environment is not development.
  if (isDevOnlyPath(normalizedPath)) {
    const envStore = useEnvironmentStore();
    if (!isDevOnlyPathAllowed(normalizedPath, authStore, envStore)) {
      if (!isLoggedIn) return { ...getHomeRoute(), query: to.query };
      return redirectWithNotice(REDIRECT_REASON.DEV_ONLY_ENV, to, { blocking: !isInitial });
    }
    if (to.path === '/ppc/management') {
      return { name: 'ppc-management-tab', params: { tab: 'campaign' }, query: to.query };
    }
  }

  // Seller Feedback is held back by VITE_ENABLE_SELLER_FEEDBACK. Hiding its nav entry is
  // cosmetic, so the flag-off build sends a direct URL home.
  if (
    !isSellerFeedbackEnabled() &&
    (normalizedPath === SELLER_FEEDBACK_PATH || normalizedPath.startsWith(`${SELLER_FEEDBACK_PATH}/`))
  ) {
    return { ...getHomeRoute(), query: to.query };
  }

  // Public pages (subject: Public, needToAuthorize: false) are always accessible.
  // Skip auth and CASL checks entirely — also handles stale localStorage abilities.
  if (to.meta.subject === 'Public') {
    if (to.meta.redirectIfLoggedIn && isLoggedIn && !to.query.inviteCode) {
      return { path: '/', query: to.query };
    }
    if (to.meta.redirectIfLoggedIn && isLoggedIn && to.query.inviteCode) {
      // An invite for another email must not silently sign the user out (in every tab): the
      // register page asks first. A matching or unknown email on register was already accepted
      // by bootstrap, so it goes to the landing route. Login and forgot-password keep the
      // sign-out (staying signed in there would mix two identities on the next login).
      const invite = resolveInviteLinkWhileSignedIn(to, {
        userEmail: authStore.user?.email,
        pendingMismatch: !!inviteMismatch.value,
      });
      if (invite.action === 'stay') return;
      if (invite.action === 'redirect') return invite.to;
      await authStore.clearTokens();
    }
    return;
  }

  // Billing gate: the account is locked until billing is resolved, so redirect
  // every protected route to /billing instead of letting pages load (and fire
  // data requests) read-only (webapp#1080 F7; matches gate.py's documented
  // intent "redirect all routes"). Two states get the full block:
  //   - hard_gate — grace period expired with no subscription;
  //   - soft_gate with gateReason 'trial' — trial ended, grace running. A
  //     dunning subscriber (gateReason 'subscription') keeps in-app access and
  //     the gentle banner treatment, so the reason must be exactly 'trial' —
  //     an absent gateReason (old cached payload) fails open and never blocks.
  // Allowlist (HARD_GATE_ALLOWED_PATHS in ./utils) = the routes needed to resolve
  // the gate or manage the account — mirrors the gateGuard interceptor's path
  // allowlist. /settings/connections is the Amazon token re-auth deep-link target
  // (stores/connectionAlerts.js); /integrations/* are the OAuth landing pages.
  // Public pages returned above.
  // Fail-open: missing/unloaded plan data must never lock a paying customer out.
  const plan = useAccountStore().currentBusinessProfile?.plan;
  const isTrialEndedSoftGate = plan?.gateState === 'soft_gate' && plan?.gateReason === 'trial';
  if (
    isLoggedIn
    && (plan?.gateState === 'hard_gate' || isTrialEndedSoftGate)
    && !isHardGateAllowedPath(normalizedPath)
  ) {
    // Explain, then redirect — a silent bounce to /billing reads as "nothing
    // happened / broken" when the user is already on the billing page and
    // clicks a locked tab. blocking:false on the initial deep-link/reload (no
    // page to hold back; notice lands on top of /billing).
    const reason = isTrialEndedSoftGate
      ? REDIRECT_REASON.BILLING_TRIAL_ENDED
      : REDIRECT_REASON.BILLING_HARD_GATE;
    return redirectWithNotice(reason, to, { blocking: !isInitial });
  }

  if (to.meta.needToAuthorize && !isLoggedIn) {
    await authStore.clearTokens();
    return loginRedirectWithReturn(to);
  }

  await authStore.getUser();

  // Connections v2 retires the /connect-accounts wizard and the dev-only hub. Here (signed in,
  // abilities loaded, anonymous visitors already sent to login with their query in ?to=) so a
  // plain User is sent home rather than to a page they cannot open.
  if (isConnectionsV2Enabled()) {
    const canManage = ability.can('read', 'Management');
    if (normalizedPath === '/connect-accounts') return connectAccountsRedirect(to, { canManage });
    if (normalizedPath === '/settings/connections-hub' || normalizedPath.startsWith('/settings/connections-hub/')) {
      return connectionsHubRedirect(to, { canManage });
    }
  }

  if (!canNavigate(to)) {
    if (isLoggedIn && to.name !== 'dashboard')
      return redirectWithNotice(REDIRECT_REASON.NO_ACCESS, to, { blocking: !isInitial });
    return loginRedirectWithReturn(to);
  }
});

router.afterEach((to, _from) => {
  // Navigation settled (confirmed, aborted or redirected) — complete the progress bar.
  finishNavigationProgress();

  // Set the viewport based on the new route.
  useViewportStore().applyViewport(to.path);

  // Give @vueuse/head time to update the document title before reporting to Google Analytics.
  // An OAuth return URL carries a one-time claim token or auth code — strip those, and pass
  // page_location explicitly too, since GA4 would otherwise read the raw document URL.
  setTimeout(() => {
    if (window.gtag) {
      window.gtag('config', googleAnalyticsId, {
        page_title: document.title,
        page_path: redactSensitiveQuery(to.fullPath),
        page_location: redactSensitiveQuery(window.location.href),
      });
    }
  }, 100); // 100ms is enough for the title to update
});

router.onError(() => {
  // A failed navigation (e.g. a chunk import error) doesn't run afterEach — clear the bar
  // here so it can never get stuck on screen. (main.js keeps its own onError for the
  // chunk-reload logic; multiple onError handlers are allowed.)
  finishNavigationProgress();
});

export { devOnlyPaths };

export default router;
